import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// interface
import { IFormsModuleProps, ILinkProps, IForm, IModalProps, IResizeOption } from '../../../interfaces/interfaces';

// module, source
import Header from '../../elements/Header/Header';
import Link from '../../elements/Link/Link';
import TileWrapper from '../../elements/TileWrapper/TileWrapper';
import { ModalContextConsumer } from '../../../contexts/modalContext';
import randomId from '../../../utils/randomId';
import { loadFormTileData, saveConfiguration } from '../../../utils/data';
import UserTileContext from '../../../contexts/userTilesContext';
import { mockFormLinks } from '../../../mock_data/mock-data';
import { breakpoints } from '../../../utils/gridUtils';
import keyboardTriggerBasic from '../../../utils/keyboard-trigger';

function FormsModule(props: IFormsModuleProps & ILinkProps): JSX.Element {
  const { headline, tileId, hideMenu, isEditable, searchUrl, resizeOptions, tileLayout } = props;

  const { t } = useTranslation();
  const tW = tileLayout?.w || 2;
  const tH = tileLayout?.h || 1;

  let maxDisplayedLinks = 3;

  if (tW === 1 && tH === 2) {
    maxDisplayedLinks = 5;
  } else if (tW === 2 && tH === 1) {
    maxDisplayedLinks = 2;
  } else if (tW === 2 && tH === 2) {
    maxDisplayedLinks = 5;
  } else if (tW === 1 && tH === 2) {
    maxDisplayedLinks = 8;
  }

  const userTiles = useContext(UserTileContext);
  const allTiles = userTiles.tiles;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasError, setError] = useState<boolean>(false);
  const [forms, setForms] = useState<IForm[]>([]);
  const [showMoreLink, setShowMoreLink] = useState<boolean>(false);

  const openFormsListModal = (openModal: (modal: IModalProps) => void) => {
    openModal({
      type: 'forms',
      key: 'forms',
      data: forms,
      message: '',
      onAbort: () => {
        //---
      },
      onConfirm: () => {
        //---
      },
    });
  };

  const loadForms = () => {
    const isLocal = !process.env.NODE_ENV || process.env.NODE_ENV === 'development';
    if (isLocal) {
      setForms(mockFormLinks);
      setIsLoading(false);
      return;
    }
    loadFormTileData(searchUrl || '', isLocal)
      .then((data) => {
        setForms(data);
      })
      .catch(() => {
        setError(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const availableResizeOptions = () => {
    // skip resizing on small (1x) grid
    if ((document.querySelector('html')?.clientWidth || 2000) < breakpoints.sm) {
      return [];
    }

    const [myTile] = allTiles.filter((ti) => ti.id === tileId);
    if (myTile) {
      return resizeOptions?.filter((ro) => {
        return ro.w !== myTile.layout.w || ro.h !== myTile.layout.h;
      });
    }
    return [];
  };

  const resizeTile = (size: IResizeOption) => {
    const updatedSet = allTiles.map((ti) => {
      if (ti.id === tileId) {
        return { ...ti, layout: { ...ti.layout, w: size.w, h: size.h } };
      }
      return { ...ti, layout: { ...ti.layout, isResizable: false } };
    });
    userTiles.setTiles(updatedSet);
    saveConfiguration(updatedSet, userTiles.settings).catch(() => {
      // TODO: better error messages
    });
  };

  useEffect(() => {
    setIsLoading(true);

    loadForms();
  }, []);

  useEffect(() => {
    setShowMoreLink(forms.length > maxDisplayedLinks);
  }, [forms, maxDisplayedLinks]);

  const loading = <p>Wird geladen...</p>;
  const errorDisplay = <p>Daten konnten nicht geladen werden</p>;
  const list = (
    <ModalContextConsumer>
      {({ openModal }) => (
	  <>
        <ul className="tile-linklist">
          {forms?.slice(0, maxDisplayedLinks).map((link) => (
            <li key={randomId('form-link-list')}>
              <Link
                linkHref={link.link}
                linkText={
                  (link.title_short &&
                    (typeof link.title_short === 'string' ? link.title_short.replaceAll('{[shy]}','&shy;') : link.title_short[0].replaceAll('{[shy]}','&shy;'))) ||
                  (typeof link.title === 'string' ? link.title.replaceAll('{[shy]}','&shy;') : link.title[0].replaceAll('{[shy]}','&shy;'))
                }
                linkClass="link link-item ic-download"
                linkTextClass="link-text"
              />
              <div className="link-note">
                ({link.media_type.length > 0 ? `${link.media_type[0].toUpperCase()}, ` : ''}
                {link.document_filesize || 0},{' '}
                {link.is_barrier_free ? t('cockpit.forms.accessible') : t('cockpit.forms.nonaccessible')})
              </div>
            </li>
          ))}
        </ul>

          {showMoreLink && (
            <Link
              onClick={() => {
                openFormsListModal(openModal);
              }}
              onKeyDown={(e) => {
                keyboardTriggerBasic(e, () => {
                  openFormsListModal(openModal);
                });
              }}
              linkText={t('cockpit.forms.openModal', 'Alle Formulare')}
              linkClass="multiple-link tile-link link ic-arrowRight"
              linkTextClass="link-text"
            />
          )}
      </>
	  )}
    </ModalContextConsumer>
  );

  return (
    <>
      <div className="tile-body">
        {headline ? (
          <Header
            headline={headline}
            tileId={tileId}
            hideMenu={hideMenu}
            isEditable={isEditable}
            resizeOptions={availableResizeOptions()}
            onResize={resizeTile}
          />
        ) : (
          ''
        )}

        <TileWrapper className="tile-padding" clickable={false}>
          {isLoading ? loading : list}
          {hasError ? errorDisplay : <p />}
        </TileWrapper>
      </div>
    </>
  );
}
export default FormsModule;
